import Page from "components/Page/Page";

const TermsAndConditions = () => {
	return (
		<Page title="Terms and Conditions">
			<h1 className="text-2xl lg:text-[32px] font-bold text-dark mb-6">
				Terms & Conditions
			</h1>
			<p className="text-sm text-dark mb-6">
				Last Updated: June 15, 2026 <br /> Please read these Terms and
				Conditions carefully before using The Accounter's services. By
				subscribing to or using our services, you agree to be bound by these
				terms. If you do not agree, please do not use our services.
			</p>
			<h2 className="text-dark font-bold mb-6">SECTION 01 - DEFINITIONS</h2>
			<p className="text-sm text-dark mb-4">
				In these Terms and Conditions, unless the context requires otherwise:
			</p>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					"The Accounter", "we", "us", or "our" refers to THE ACCOUNTER, trading
					as The Accounter, a company registered in the UAE.
				</li>
				<li>
					"Client", "you", or "your" refers to the individual or business entity
					that subscribes to or uses our services.
				</li>
				<li>
					"Services" refers to the accounting, bookkeeping, VAT filing, tax
					compliance, financial reporting, and related services provided by The
					Accounter.
				</li>
				<li> "Platform" refers to our website and mobile application. </li>
				<li>
					"Subscription" refers to your paid plan for access to our services.
				</li>
				<li>
					"Deliverables" refers to financial reports, tax returns, filings, and
					other outputs produced by The Accounter.
				</li>
				<li>
					"Client Data" refers to all documents, records, invoices, and
					information you provide to us.
				</li>
			</ul>
			<h2 className="text-dark font-bold mb-6">
				SECTION 02 - SCOPE OF SERVICES
			</h2>
			<p className="text-sm text-dark mb-4">
				The Accounter provides professional accounting and bookkeeping services
				to businesses and individuals in the UAE. Our services include, but are
				not limited to:
			</p>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>Bookkeeping and maintenance of financial records</li>
				<li>
					VAT registration, filing, and compliance (UAE Federal Tax Authority)
				</li>
				<li>Corporate tax filing and compliance</li>
				<li>Financial statement preparation and reporting</li>
				<li>Invoice processing and reconciliation</li>
				<li>Payroll processing (where included in your plan)</li>
			</ul>
			<p className="text-sm text-dark mb-6">
				The exact scope of services applicable to your subscription is defined
				in your service plan selected at the time of sign-up. Additional
				services not included in your plan may be provided at additional cost,
				by prior written agreement.
			</p>
			<h2 className="text-dark font-bold mb-6">
				SECTION 03 - CLIENT OBLIGATIONS
			</h2>
			<p className="text-sm text-dark mb-4">
				To enable us to provide services effectively and accurately, you agree
				to:
			</p>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Provide complete, accurate, and up-to-date financial records,
					documents, and information in a timely manner.
				</li>
				<li>
					Upload invoices, receipts, and supporting documents via the app or
					agreed channels within specified timeframes.
				</li>
				<li>
					Notify us promptly of any material changes to your business,
					structure, or financial circumstances.
				</li>
				<li>
					Respond to queries from our team within a reasonable timeframe
					(typically 3 business days).
				</li>
				<li>
					Review and approve Deliverables before submission to regulatory
					authorities where required.
				</li>
				<li>
					Maintain your own copies of all financial records and documents.
				</li>
			</ul>
			<div className="bg-yellow-50 border-s-4 border-yellow-500 p-4 mb-6">
				<p className="text-sm text-dark">
					<strong>IMPORTANT:</strong> Our ability to deliver accurate and timely
					services is directly dependent on the completeness and accuracy of the
					information and documents you provide. We are not liable for errors,
					penalties, or missed deadlines resulting from incomplete, inaccurate,
					or late provision of Client Data.
				</p>
			</div>
			<h2 className="text-dark font-bold mb-6">
				SECTION 04 - FEES, BILLING & PAYMENT
			</h2>
			<h3 className="text-dark font-bold mb-4">4.1 Subscription Fees</h3>
			<p className="text-sm text-dark mb-6">
				Subscription fees are as set out in your selected plan at the time of
				sign-up, starting from AED 250 per month. Fees are subject to change
				with 30 days' written notice.
			</p>
			<h3 className="text-dark font-bold mb-4">4.2 Invoicing</h3>
			<p className="text-sm text-dark mb-6">
				Invoices are issued monthly in advance. Payment is due within 7 days of
				the invoice date unless otherwise agreed in writing.
			</p>
			<h3 className="text-dark font-bold mb-4">4.3 Late Payments</h3>
			<p className="text-sm text-dark mb-6">
				Payments not received within 14 days of the due date may result in
				suspension of services. We reserve the right to charge reasonable late
				payment interest in accordance with UAE law.
			</p>
			<h3 className="text-dark font-bold mb-4">4.4 VAT</h3>
			<p className="text-sm text-dark mb-6">
				All fees are exclusive of VAT. UAE VAT at the applicable rate will be
				added to invoices where applicable.
			</p>
			<h3 className="text-dark font-bold mb-4">4.5 Additional Services</h3>
			<p className="text-sm text-dark mb-6">
				Additional services outside your subscription plan will be quoted and
				agreed in writing before work commences.
			</p>
			<h2 className="text-dark font-bold mb-6">SECTION 05 - ERROR GUARANTEE</h2>
			<p className="text-sm text-dark mb-6">
				The Accounter stands behind the accuracy of its work. Where we make an
				error that directly results in a penalty or fine imposed by a UAE
				regulatory authority, we will cover that fine subject to the conditions
				below.
			</p>
			<h3 className="text-dark font-bold mb-4">5.1 What We Cover</h3>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					The error was solely caused by The Accounter, with no contribution
					from incomplete, inaccurate, or late Client Data.
				</li>
				<li>You provided all required information accurately and on time.</li>
				<li>
					You notified us of the fine within 14 calendar days of receiving the
					official notice.
				</li>
				<li>
					The fine relates to a service included in your active subscription
					plan.
				</li>
				<li>
					The error occurred during an active subscription period with all
					payments up to date.
				</li>
			</ul>
			<h3 className="text-dark font-bold mb-4">5.2 What Is Not Covered</h3>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>Incomplete, inaccurate, or late information from the client.</li>
				<li>Undisclosed transactions or obligations.</li>
				<li>
					Interest charges, commercial losses, loss of profit, or consequential
					damages.
				</li>
				<li>Changes in UAE tax law or regulations after the filing date.</li>
				<li> Client decisions made contrary to our written advice. </li>
				<li>Non-UAE jurisdictions and foreign tax authorities.</li>
				<li>Liabilities existing before becoming a client.</li>
				<li>Services outside the active subscription plan.</li>
			</ul>
			<h3 className="text-dark font-bold mb-4">5.3 Claims Process</h3>
			<ol className="list-decimal list-inside text-sm text-dark mb-6 space-y-2">
				<li>Notify us in writing at hello@theaccounter.com within 14 days.</li>
				<li>Provide a copy of the official penalty notice.</li>
				<li>
					We will investigate and confirm responsibility within 10 business
					days.
				</li>
				<li>
					If confirmed, we will reimburse the fine or pay it directly within 15
					business days.
				</li>
			</ol>
			<h3 className="text-dark font-bold mb-4">5.4 Maximum Liability</h3>
			<p className="text-sm text-dark mb-6">
				Our total liability under this guarantee in any 12-month period shall
				not exceed the total fees paid by you during that same period.
			</p>
			<h2 className="text-dark font-bold mb-6">
				SECTION 06 - INTELLECTUAL PROPERTY
			</h2>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					All templates, methodologies, processes, software, and platform
					content remain the exclusive intellectual property of The Accounter.
				</li>
				<li>
					Deliverables created specifically for you become yours once full
					payment has been received.
				</li>
				<li>
					You grant us a limited, non-exclusive licence to use Client Data
					solely to provide services.
				</li>
			</ul>
			<h2 className="text-dark font-bold mb-6">SECTION 07 - CONFIDENTIALITY</h2>
			<p className="text-sm text-dark mb-6">
				Both parties agree to treat all information shared during the engagement
				as confidential and not disclose it to third parties except where
				required by law or regulatory authority.
			</p>
			<p className="text-sm text-dark mb-6">
				We handle all Client Data in accordance with our Privacy Policy, and all
				team members are bound by confidentiality obligations.
			</p>
			<h2 className="text-dark font-bold mb-6">
				SECTION 08 - LIMITATION OF LIABILITY
			</h2>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Except as expressly provided in Section 5, our total liability shall
					not exceed the total fees paid in the three months preceding the
					claim.
				</li>
				<li>
					We shall not be liable for indirect, consequential, special, or
					punitive damages, loss of profit, loss of data, or business
					interruption.
				</li>
				<li>
					Nothing limits liability for fraud, wilful misconduct, or death or
					personal injury caused by negligence.
				</li>
			</ul>
			<h2 className="text-dark font-bold mb-6">SECTION 09 - TERMINATION</h2>
			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Either party may terminate the subscription with 30 days' written
					notice.
				</li>
				<li>
					We may suspend or terminate services immediately for non-payment,
					misleading information, or legal requirements.
				</li>
				<li>
					Upon termination, Deliverables and Client Data will be provided within
					14 days, subject to payment of outstanding fees.
				</li>
			</ul>
			<h2 className="text-dark font-bold mb-6">SECTION 10 - FORCE MAJEURE</h2>
			<p className="text-sm text-dark mb-6">
				We shall not be liable for delays or failures resulting from events
				beyond our reasonable control, including natural disasters, government
				actions, cyberattacks, telecommunications failures, pandemics, or
				similar events.
			</p>
			<p className="text-sm text-dark mb-6">
				If such an event prevents service delivery for more than 30 consecutive
				days, either party may terminate the affected services without further
				liability, subject to payment for work already completed.
			</p>
			<h2 className="text-dark font-bold mb-6">
				SECTION 11 - GOVERNING LAW & DISPUTE RESOLUTION
			</h2>
			<p className="text-sm text-dark mb-6">
				These Terms and Conditions are governed by the laws of the United Arab
				Emirates and the Emirate of Dubai.
			</p>
			<p className="text-sm text-dark mb-6">
				Any dispute shall first be referred to good-faith negotiation. If
				unresolved within 30 days, disputes shall be submitted to the courts of
				Dubai, UAE, which shall have exclusive jurisdiction.
			</p>
			<h2 className="text-dark font-bold mb-6">
				SECTION 12 - CHANGES TO THESE TERMS
			</h2>
			<p className="text-sm text-dark mb-6">
				We may update these Terms and Conditions from time to time. Material
				changes will be communicated by email or through the platform at least
				30 days before they take effect.
			</p>
			<h2 className="text-dark font-bold mb-6">SECTION 13 - CONTACT US</h2>
			<p className="text-sm text-dark mb-2">
				<strong>The Accounter</strong>
			</p>
			<p className="text-sm text-dark mb-2">
				Fintech Hive, Zone D, Level 1 Gate Avenue, DIFC, Dubai, UAE
			</p>
			<p className="text-sm text-dark mb-2">
				Email: customersupport@theaccounter.com
			</p>
			<p className="text-sm text-dark mb-6">
				Website:
				<span className="text-blue-500 underline">www.theaccounter.com</span>
			</p>
		</Page>
	);
};

export default TermsAndConditions;
